import { Radio, Segmented, Select, Space, Switch, Typography } from "antd";
import { LineChartOutlined, BarChartOutlined } from "@ant-design/icons";
import { usePlotContext } from "../PlotContext";
import type { GraphFormState } from "../plotState";

const { Text } = Typography;

interface GraphTypeStepProps {
  graph: GraphFormState;
  graphIndex: number;
  onChange: (patch: Partial<GraphFormState>) => void;
}

export function GraphTypeStep({ graph, graphIndex, onChange }: GraphTypeStepProps) {
  const { state, metrics } = usePlotContext();

  const stackableBarsBefore = state.graphs
    .slice(0, graphIndex)
    .filter((g) => g.chartType === "bar" && !g.stacked);
  const showStackOption =
    graph.chartType === "bar" && stackableBarsBefore.length > 0;

  return (
    <div className="awm-step-body">
      <Text type="secondary" style={{ fontSize: 12 }}>
        Choose how this graph's data will be visualised.
      </Text>
      <Radio.Group
        value={graph.chartType}
        onChange={(e) => {
          const v = e.target.value;
          onChange(
            v === "line"
              ? { chartType: "line", stacked: false, stackOnGraphId: null }
              : { chartType: "bar" },
          );
        }}
        style={{ display: "flex", gap: 10, marginTop: 4 }}
      >
        <Radio.Button
          value="line"
          style={{
            flex: 1, height: 72, display: "flex",
            flexDirection: "column", alignItems: "center", justifyContent: "center",
          }}
        >
          <LineChartOutlined style={{ fontSize: 24 }} />
          <span style={{ fontSize: 12, marginTop: 4 }}>Line</span>
        </Radio.Button>
        <Radio.Button
          value="bar"
          style={{
            flex: 1, height: 72, display: "flex",
            flexDirection: "column", alignItems: "center", justifyContent: "center",
          }}
        >
          <BarChartOutlined style={{ fontSize: 24 }} />
          <span style={{ fontSize: 12, marginTop: 4 }}>Bar</span>
        </Radio.Button>
      </Radio.Group>

      <div
        style={{
          padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8,
          background: "#fafbfc", display: "flex", justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Text strong style={{ fontSize: 12 }}>Y-axis</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Assign this series to the left or right axis.
          </Text>
        </div>
        <Segmented
          size="small"
          value={graph.yAxis}
          onChange={(v) => onChange({ yAxis: v as "left" | "right" })}
          options={[
            { label: "Left", value: "left" },
            { label: "Right", value: "right" },
          ]}
        />
      </div>

      {showStackOption && (
        <div
          style={{
            marginTop: 8, padding: "8px 10px",
            border: `1px solid ${graph.stacked ? "#1677ff" : "#e0e5eb"}`,
            borderRadius: 8, background: graph.stacked ? "#f0f5ff" : "#fafbfc",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <Text strong style={{ fontSize: 12 }}>Stacked bar</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Combine this bar with an existing bar graph.
              </Text>
            </div>
            <Switch
              size="small"
              checked={graph.stacked}
              onChange={(checked) =>
                onChange({
                  stacked: checked,
                  stackOnGraphId: checked ? (stackableBarsBefore[0]?.id ?? null) : null,
                })
              }
            />
          </div>
          {graph.stacked && (
            <div style={{ marginTop: 8 }}>
              <Space size={8} align="center">
                <Text style={{ fontSize: 11 }}>Stack on</Text>
                <Select
                  size="small"
                  value={graph.stackOnGraphId}
                  onChange={(v) => onChange({ stackOnGraphId: v })}
                  style={{ minWidth: 160 }}
                >
                  {stackableBarsBefore.map((bg) => {
                    const idx = state.graphs.indexOf(bg) + 1;
                    const mName = metrics.find((m) => m.id === bg.selectedMetricId)?.name ?? "Untitled";
                    return (
                      <Select.Option key={bg.id} value={bg.id}>
                        Graph {idx} – {mName}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Space>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
